import {ServiceUnavailableError} from '@hm/fundamental'
import React from 'react'
import {ErrorBoundary} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router'

import {FeatureTranslationsLoader} from '../../localization-loaders'

import {FEATURE_ERROR_BOUNDARY_NAMESPACE, featureErrorBoundaryTranslations} from './localization'

export interface FeatureErrorBoundaryProps {
  children: React.ReactNode
}

const FeatureErrorFallback = ({resetErrorBoundary}: {resetErrorBoundary: () => void}) => {
  const {t} = useTranslation(FEATURE_ERROR_BOUNDARY_NAMESPACE)
  return (
    <ServiceUnavailableError
      title={t('title')}
      description={t('description')}
      refreshText={t('refreshAction')}
      onRefresh={resetErrorBoundary}
    />
  )
}

/**
 * Keeps a crash inside a feature's page content, so the surrounding app (e.g. the Suite header and
 * hierarchy picker) stays usable. Navigating elsewhere clears the error.
 * Brings its own texts, loaded only once an error is shown.
 */
export const FeatureErrorBoundary = ({children}: FeatureErrorBoundaryProps) => {
  const {pathname} = useLocation()

  return (
    <ErrorBoundary
      fallbackRender={({resetErrorBoundary}) => (
        <FeatureTranslationsLoader translations={featureErrorBoundaryTranslations}>
          <FeatureErrorFallback resetErrorBoundary={resetErrorBoundary} />
        </FeatureTranslationsLoader>
      )}
      resetKeys={[pathname]}
    >
      {children}
    </ErrorBoundary>
  )
}
